import { useEffect, useState, createRef, useRef } from 'react';

/* ========================================================================= */
/* Generic */
/* ========================================================================= */

/**
 * Access a previous state value
 * @param {any} value
 * @returns {any}
 */
export const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

/**
 * A version of useEffect that will only fire on subsequent updates
 * @param {function} callback
 * @param {array} dependencies
 */
export const useEffectOnUpdate = (callback, dependencies) => {
    const didMount = useRef(false);
    useEffect(() => {
        if (didMount.current) {
            callback(dependencies);
        } else {
            didMount.current = true;
        }
    }, dependencies);
};

/**
 * @hook useRefs
 * @param {number} total
 * @return {array}
 *
 * Generates a list of references. A reference can be added on a React element by using
 * the following syntax: `<MyComponent ref={myRefs[2]} />`
 */
export const useRefs = total => {
    const [ refs, setRefs ] = useState([]);

    useEffect(() => {
        setRefs(_refs => Array(total).fill().map((_, i) => _refs[i] || createRef(null)));
    }, [ total ]);

    return refs;
};

/* ========================================================================= */
/* Array manipulation */
/* ========================================================================= */

/**
 * @hook useRandom
 * @param {array} items
 * @param {array} dependencies (optional)
 * @return {any}
 *
 * Pulls a random item from an array and returns it.
 * 1. The initial renders always return the first item in the array to avoid
 *    conflicts between the server and browser renderings.
 * 2. Randomizes the return item when rendered in the browser.
 */
export const useRandom = (items, dependencies = []) => {
    const [ randomizedItem, setRandomizedItem ] = useState(items[0]); /* [1] */

    useEffect(() => { /* [2] */
        const randomizedIndex = Math.floor(Math.random() * items.length);
        setRandomizedItem(items[randomizedIndex]);
    }, dependencies);

    return randomizedItem;
};

/* ========================================================================= */
/* Number manipulation */
/* ========================================================================= */

/**
 * @const useIndex
 * @param  {int} initialIndex
 * @param  {int} total
 * @return {array}
 *
 * Custom hook managing indexes. An index cannot go below a 0 and above a specified total.
 * 1. If safeSetIndex was executed successfully, let's return true to the caller.
 */
export const useIndex = (initialIndex, total) => {
    const [ index, setIndex ] = useState(initialIndex);
    const safeSetIndex = newIndex => {
        if (newIndex >= 0 && newIndex < total) {
            return (setIndex(newIndex), true); /* [1] */
        } else {
            return false;
        }
    };

    return [ index, safeSetIndex ];
};
